import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { LogIn, AlertCircle, Eye, EyeOff, X, Mail } from 'lucide-react';
import './AdminLogin.css';

const AdminLogin = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle'); // 'idle'|'loading'|'success'|'error'
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);

      if (result.success) {
        navigate('/admin/dashboard');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Errore di accesso');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus('loading');
    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotStatus('success');
    } catch (err) {
      setForgotStatus('error');
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-login-header">
          <div className="admin-logo">
            <img src="/logos/meccaflex-isometric-logo.svg" alt="MeccaFlex" />
          </div>
          <h1>{t('admin.login.title')}</h1>
          <p>{t('admin.login.subtitle')}</p>
        </div>

        {error && (
          <div className="admin-alert admin-alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="admin-form-group">
            <label htmlFor="email">{t('admin.login.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@meccaflex.com"
              required
              autoFocus
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="password">{t('admin.login.password')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#a1a1aa',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Remember me checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#f97316',
                cursor: 'pointer',
                flexShrink: 0
              }}
            />
            <label
              htmlFor="rememberMe"
              style={{ color: '#a1a1aa', fontSize: '14px', cursor: 'pointer', userSelect: 'none' }}
            >
              Ricordami per 30 giorni
            </label>
          </div>

          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={loading}
          >
            {loading ? (
              t('admin.login.loggingIn')
            ) : (
              <>
                <LogIn size={20} />
                {t('admin.login.loginButton')}
              </>
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={() => { setShowForgotModal(true); setForgotEmail(''); setForgotStatus('idle'); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#f97316',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Hai dimenticato la password?
            </button>
          </div>
        </form>

        <div className="admin-login-footer">
          <p>© 2025 MeccaFlex. All rights reserved.</p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowForgotModal(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-modal-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1c1c1e', borderRadius: '12px', padding: '2rem',
              width: '100%', maxWidth: '400px', margin: '1rem',
              border: '1px solid #3f3f46'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 id="forgot-modal-title" style={{ color: '#fff', fontSize: '18px', margin: 0 }}>
                Reset Password
              </h2>
              <button
                onClick={() => setShowForgotModal(false)}
                aria-label={t('admin.common.close')}
                style={{
                  background: 'none', border: 'none', color: '#a1a1aa',
                  cursor: 'pointer', padding: '4px', display: 'flex'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {forgotStatus === 'success' ? (
              <div style={{
                background: '#10b98120', border: '1px solid #10b981',
                borderRadius: '8px', padding: '1rem', color: '#10b981', fontSize: '14px'
              }}>
                Se l'email esiste nel sistema, riceverai un link di reset.
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                {forgotStatus === 'error' && (
                  <div style={{
                    background: '#ef444420', border: '1px solid #ef4444',
                    borderRadius: '8px', padding: '0.75rem', color: '#ef4444',
                    fontSize: '14px', marginBottom: '1rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}>
                    <AlertCircle size={16} />
                    Si è verificato un errore. Riprova più tardi.
                  </div>
                )}
                <div className="admin-form-group">
                  <label htmlFor="forgot-email" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Mail size={16} /> Indirizzo Email
                  </label>
                  <input
                    type="email"
                    id="forgot-email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="admin@meccaflex.com"
                    required
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="admin-btn admin-btn-secondary"
                    style={{ flex: 1 }}
                  >
                    {t('admin.login.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="admin-btn admin-btn-primary"
                    disabled={forgotStatus === 'loading'}
                    style={{ flex: 1 }}
                  >
                    {forgotStatus === 'loading' ? 'Invio...' : 'Invia Link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
